import React, { useState, useRef, useEffect } from 'react'
import {
  createStyles,
  makeStyles,
  withStyles,
  useTheme,
  useMediaQuery,
  TextField,
  MenuItem,
  Box,
  Button,
  CircularProgress,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Input,
  Grid,
  Divider,
  IconButton,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'

import { DatePicker } from '@material-ui/pickers'
import isValid from 'date-fns/isValid'
import { Prompt } from 'react-router-dom'
import format from 'date-fns/format'
import isAfter from 'date-fns/isAfter'
import { toast } from 'react-toastify'
import { omit, omitBy, isNull, isString, throttle } from 'lodash'
import { useTranslation } from 'react-i18next'
import { Formik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { updateUserInfo } from 'src/store/slices/authSlice'
import FocusError from './FocusError'
import { ERadio } from 'src/mobile/themes/components/ETextField'
import { ReactComponent as RightArrowIcon } from 'src/mobile/assets/icons/arrow-ios-right.svg'
import { ReactComponent as Profile } from 'src/mobile/assets/icons/profile_icon.svg'
import { ReactComponent as Calendar } from 'src/mobile/assets/icons/calendar_month.svg'
import {
  LABEL_WIDTH,
  INPUT_WIDTH,
  MOBILE_INPUT_WIDTH,
  DIALING_CODES,
  ID_TYPES,
  BIRTHDAY_FORMAT,
} from 'src/utils/constant'
import { encryptId, parseValid, formatDialingCode, getBirthDate } from 'src/utils'
import { oriSchema } from 'src/utils/schema'
import { useTranslateFormErrors } from 'src/hooks'
import FixedButtonWrapper from 'src/mobile/components/FixedButtonWrapper'
import EDrawer from 'src/mobile/themes/components/EDrawer'
import ChangePhoneForm from 'src/mobile/components/ChangePhoneForm'

const useStyles = makeStyles((theme) =>
  createStyles({
    form: {
      backgroundColor: theme.palette.background.paper,
      paddingBottom: theme.spacing(2),
    },
    submitButton: {
      height: theme.spacing(5),
    },
    textField: {
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(1.5),
      '& .MuiInputLabel-outlined': {
        display: 'flex',
        flexDirection: 'row-reverse',
      },
      '& .MuiInputLabel-formControl': {
        display: 'flex',
        flexDirection: 'row-reverse',
      },
      '& .MuiFormLabel-asterisk': {
        color: theme.palette.secondary.main,
      },
    },
    textField2: {
      '& .MuiInputLabel-outlined': {
        display: 'flex',
        flexDirection: 'row-reverse',
      },
      '& .MuiFormLabel-asterisk': {
        color: theme.palette.secondary.main,
      },
    },
    underline: {
      borderBottom: 'none',
    },

    radioGroup: {
      marginTop: theme.spacing(2),
      borderBottom: `1px solid ${theme.palette.grey[50]}`,
    },
    arrowIcon: {
      width: theme.spacing(2.5),
      height: theme.spacing(2.5),
      '& path': {
        fill: theme.palette.grey[100],
      },
    },
    calendar: {
      width: theme.spacing(2.5),
      height: theme.spacing(2.5),
    },
    formControlLabel: {
      color: theme.palette.grey[200],
    },
    infoLabel: {
      color: '#4B5563',
      paddingTop: theme.spacing(1),
    },
    infoText: {
      color: theme.palette.text.primary,
      paddingTop: theme.spacing(1),
      fontSize: theme.typography.body1.fontSize,
    },
    infoTextLabel: {
      color: theme.palette.grey[50],
      paddingTop: theme.spacing(1),
      fontSize: theme.typography.body1.fontSize,
    },
    editAccountButton: {
      fontSize: theme.typography.body1.fontSize,
      fontWeight: 600,
      textDecoration: 'underline',
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
    },
    emailBox: {
      wordBreak: 'break-all',
    },
    asterisk: {
      color: 'red',
    },
  }),
)

const initialValues = {
  dialingCode: DIALING_CODES[0].value,
  nameEn: '',
  nameCn: '',
  gender: '0',
  identifyId: '',
  identifyType: 1,
  birthday: new Date(),
  phone: '',
  email: '',
  placeOfIssuance: '',
  whatsAppId: '',
  weChatId: '',
  userStatus: '0',
}

const CustomDatePicker = withStyles((theme) => ({
  root: {
    'label + &': {
      marginTop: theme.spacing(2),
      marginLeft: theme.spacing(LABEL_WIDTH),
    },
    borderRadius: theme.spacing(1),
    position: 'relative',
    backgroundColor: theme.palette.common.white,
    fontSize: theme.typography.body2.fontSize,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    minWidth: theme.spacing(INPUT_WIDTH),
    [theme.breakpoints.down('xs')]: {
      minWidth: theme.spacing(MOBILE_INPUT_WIDTH),
    },
  },
}))(DatePicker)

const UserInfoForm = ({ userInfo, onConfirm, onCancel, fromOrder, isFilled }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const resolveRef = useRef(null)
  const rejectRef = useRef(null)
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  const { updateUserInfoStatus } = useSelector((state) => state.auth)
  const [open, setOpen] = useState(false)
  const [activeDialogKey, setActiveDialogKey] = useState('phone')

  const [openDialog, setOpenDialog] = useState(false)

  const schema = oriSchema(t).pick([
    'nameEn',
    'nameCn',
    'gender',
    'identifyType',
    'identifyId',
    'birthday',
    'email',
    'dialingCode',
    'placeOfIssuance',
    'phone',
    'userStatus',
    'weChatId',
    'whatsAppId',
  ])
  const DIALING_Label = {
    profile: t('profile.add_profile'),
    phone: t('profile.edit') + t('profile.tel_number'),
    email: t('profile.edit') + t('form.address_email'),
    WhatsApp: t('profile.edit') + 'WhatsApp',
    WeChat: t('profile.edit') + 'WeChat',
    changePassword: t('profile.change_pwd'),
  }

  const handleConfirm = (values) => {
    onConfirm && onConfirm(values)
  }
  const [openDrawer, setOpenDrawer] = useState({
    phone: false,
    email: false,
  })
  const handleClose = () => {
    setOpen(false)
    resolveRef.current = null
    rejectRef.current = null
  }
  const handleOpen = () => {
    setOpen(true)
  }
  const toggleDrawer = (anchor, open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return
    }

    if (isMobile) {
      setOpenDrawer({ ...openDrawer, [anchor]: open })
    } else {
      setOpenDialog(true)
      setActiveDialogKey(anchor)
    }
  }
  const handleCancelSubmit = () => {
    rejectRef.current && rejectRef.current()
    handleClose()
  }

  const handleResolveSubmit = () => {
    resolveRef.current && resolveRef.current()
    handleClose()
  }

  const identifyPlaceholder = (identifyType) => {
    switch (identifyType) {
      case 1:
        return 'Z123456(7)'
      case 2:
        return '1234567(8)'
      case 3:
        return 'E12345678'
      default:
        return '440102198404289701'
    }
  }

  const ACCOUNTS = [
    {
      key: 'phone',
      label: 'profile.tel_number',
    },
    {
      key: 'email',
      label: 'form.address_email',
    },
  ]
  return (
    <Formik
      initialValues={{
        ...initialValues,
        ...omitBy(userInfo, isNull),
        userStatus: userInfo?.userStatus === '1' ? true : false,
        birthday: isString(userInfo?.birthday)
          ? parseValid(userInfo?.birthday, BIRTHDAY_FORMAT, new Date())
          : null,
        whatsAppId: userInfo?.whatsAppId || userInfo?.phone || '',
      }}
      // from add order page user info form should validate phone and email field.
      validationSchema={
        fromOrder ? schema.omit(['email', 'phone']) : schema.omit(['phone', 'dialingCode', 'email'])
      }
      onSubmit={throttle(async (values, actions) => {
        if (updateUserInfoStatus === 'pending') return
        // if user info is not completed, popup note dialog for user to double confirm.
        if (!userInfo?.isCompleteInfo)
          await new Promise((resolve, reject) => {
            handleOpen()
            // async submission can block promise, so can save promise resolve and reject inside ref, execution them after dialog actions
            resolveRef.current = resolve
            rejectRef.current = reject
          })
        const { gender, userStatus, birthday } = values
        // phone and email field should not be updated by user info update api, they will be updated by other independent api.
        const pureValues = omit(
          {
            ...values,
            id: userInfo?.id,
            gender: Number(gender),
            userStatus: userStatus ? '1' : '0',
            isCompleteInfo: true,
            birthday: format(birthday, BIRTHDAY_FORMAT),
          },
          ['email', 'phone', 'dialingCode'],
        )
        await dispatch(updateUserInfo(pureValues)).unwrap()
        // resetForm can update the form's initialValues and set dirty true.
        actions.resetForm({ values })
        toast.success(t('tips.change_info_successfully'))
        handleConfirm(pureValues)
      }, 1000)}
    >
      {(props) => {
        const {
          values,
          // setValues,
          setFieldValue,
          touched,
          errors,
          handleChange,
          handleSubmit,
          // handleReset,
          dirty,
          setFieldTouched,
          // isSubmitting,
          // resetForm,
          // initialValues,
        } = props

        useEffect(() => {
          if (fromOrder) {
            setFieldTouched('phone')
          }
        }, [])
        // useEffect(() => {
        //   if (fromOrder && userInfo?.phone) {
        //     setFieldValue('phone', userInfo?.phone)
        //   }
        // }, [userInfo?.phone])
        const isError = (field) => touched[field] && Boolean(errors[field])
        const errorText = (field) => touched[field] && errors[field]
        useTranslateFormErrors(errors, touched, setFieldTouched)

        // const onAccountUpdate = async (accountValues) => {
        //   resetForm({ values: { ...initialValues, ...accountValues } })
        //   setValues({ ...values, ...accountValues })
        // }
        return (
          <>
            <form className={classes.form} onSubmit={handleSubmit} noValidate id="form1">
              {/* show dialog before leaving page when form is dirty.  */}
              <Prompt when={dirty} message={t('are_you_sure.you_have_unsaved_form')} />
              {!isFilled && (
                <>
                  <Box color="secondary.main" fontSize={16} display="flex">
                    <Profile />
                    {t('profile.add_profile_tip')}
                  </Box>
                  <Box my={1}>
                    <sup style={{ color: '#F2512D' }}>*</sup>
                    {t('profile.required')}
                  </Box>
                  <Box display="flex" mb={2}>
                    <TextField
                      className={classes.textField}
                      label={t('form.en_name')}
                      name="nameEn"
                      fullWidth
                      variant="outlined"
                      value={values.nameEn}
                      onChange={handleChange}
                      placeholder={t('form.en_name_default')}
                      type="text"
                      error={isError('nameEn')}
                      required
                      disabled={userInfo?.isCompleteInfo}
                      helperText={isError('nameEn') ? errorText('nameEn') : t('common.must_same')}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      style={{ marginRight: '8px' }}
                    />
                    <TextField
                      className={classes.textField}
                      label={t('form.zh_name')}
                      name="nameCn"
                      fullWidth
                      variant="outlined"
                      value={values.nameCn}
                      onChange={handleChange}
                      placeholder={t('form.zh_name_default')}
                      type="text"
                      error={isError('nameCn')}
                      required={values.identifyType === 0}
                      disabled={userInfo?.isCompleteInfo}
                      helperText={isError('nameCn') ? errorText('nameCn') : t('common.must_same')}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Box>
                  <TextField
                    select
                    className={classes.textField2}
                    labelId="identify-id-select-label"
                    id="identify-id-type-select"
                    name="identifyType"
                    error={isError('identifyType')}
                    required
                    label={t('form.id_type')}
                    style={{ width: '50%' }}
                    variant="outlined"
                    value={values.identifyType}
                    onChange={handleChange}
                    onBlur={() => {
                      if (values.identifyType === '0' && values.identifyId?.length === 18) {
                        setFieldValue(
                          'birthday',
                          parseValid(getBirthDate(values.identifyId), BIRTHDAY_FORMAT, new Date()),
                        )
                      }
                    }}
                    input={<Input />}
                    disabled={userInfo?.isCompleteInfo}
                    IconComponent={() => <RightArrowIcon className={classes.arrowIcon} />}
                  >
                    {ID_TYPES.map((idType) => (
                      <MenuItem key={idType.value} value={idType.value}>
                        {t(idType.label)}
                      </MenuItem>
                    ))}
                  </TextField>
                  {errorText('identifyType')}
                  <Grid container spacing={2}>
                    <Grid item xs={values.identifyType === 3 ? 6 : 12}>
                      {/* <InputLabel shrink required>
                        {t('form.id_number')}
                      </InputLabel> */}
                      <TextField
                        className={classes.textField}
                        label={t('form.id_number')}
                        id="identify-id-field"
                        name="identifyId"
                        variant="outlined"
                        fullWidth
                        FormHelperTextProps={{
                          classes: {
                            root: classes.helperText,
                          },
                        }}
                        value={
                          userInfo?.isCompleteInfo
                            ? encryptId(values.identifyId, values.identifyType)
                            : values.identifyId
                        }
                        onChange={handleChange}
                        onInput={(e) => {
                          if (values.identifyType === 0 && e.target.value.length === 18) {
                            setFieldValue(
                              'birthday',
                              parseValid(getBirthDate(e.target.value), BIRTHDAY_FORMAT, new Date()),
                            )
                          }
                        }}
                        placeholder={identifyPlaceholder(values.identifyType)}
                        type="text"
                        error={isError('identifyId')}
                        required
                        // disabled={userInfo?.isCompleteInfo}
                        helperText={
                          isError('identifyId')
                            ? errorText('identifyId')
                            : t('tips.check_id_number_for_report')
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                    {values.identifyType === 3 && (
                      <Grid item xs={6}>
                        <TextField
                          className={classes.textField}
                          label={t('form.place_of_issue')}
                          id="place-of-issuance-field"
                          name="placeOfIssuance"
                          variant="outlined"
                          fullWidth
                          value={values.placeOfIssuance}
                          onChange={handleChange}
                          placeholder={t('validation.please_enter', { field: '$t(form.place_of_issue)' })}
                          type="text"
                          error={isError('placeOfIssuance')}
                          required={values.identifyType === 3}
                          disabled={values.identifyType === 3 && userInfo?.isCompleteInfo}
                          helperText={errorText('placeOfIssuance')}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                  <TextField
                    select
                    className={classes.textField}
                    fullWidth
                    error={isError('gender')}
                    required
                    variant="outlined"
                    name="gender"
                    style={{ width: '50%' }}
                    label={t('form.gender')}
                    value={values.gender}
                    onChange={handleChange}
                    id="gender-radio"
                    disabled={userInfo?.isCompleteInfo}
                  >
                    <MenuItem value="0" control={<ERadio size="small" color="secondary" />}>
                      {t('form.male')}
                    </MenuItem>
                    <MenuItem value="1" control={<ERadio size="small" color="secondary" />}>
                      {t('form.female')}
                    </MenuItem>
                    {errorText('gender')}
                  </TextField>
                  <Box border="1px solid #D7D8DA" borderRadius={8} px={1}>
                    <CustomDatePicker
                      className={classes.textField}
                      classes={{ underline: classes.underline }}
                      label={t('form.birthday')}
                      id="birthday-field"
                      name="birthday"
                      placeholder={t('form.birthday_default')}
                      openTo="year"
                      views={['year', 'month', 'date']}
                      format={BIRTHDAY_FORMAT}
                      value={values.birthday}
                      onChange={(date) => setFieldValue('birthday', date)}
                      shouldDisableDate={(day) => isAfter(day, new Date())}
                      fullWidth
                      maxDate={new Date()}
                      error={isError('birthday')}
                      required
                      disabled={userInfo?.isCompleteInfo || values.identifyType === '0'}
                      helperText={errorText('birthday')}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{
                        endAdornment: <Calendar className={classes.calendar} />,
                      }}
                      okLabel={t('common.confirm')}
                      cancelLabel={t('common.cancel')}
                    />
                  </Box>
                </>
              )}

              {userInfo && (
                <Box fontWeight={600} fontSize={isMobile ? 16 : 20} paddingTop={2}>
                  {t('commom.contact')}
                </Box>
              )}
              {/* {initialValues.phone ? (
                <TextField
                  className={classes.textField}
                  label={t('form.mobile_phone')}
                  id="phone-field"
                  name="phone"
                  fullWidth
                  value={encryptPhone(values.phone)}
                  placeholder={t('validation.please_enter', { field: '$t(form.mobile_phone)' })}
                  error={isError('phone')}
                  required={fromOrder}
                  disabled
                  helperText={errorText('phone')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    startAdornment: values.phone && (
                      <Box flexShrink={0} fontSize="body2.fontSize" color="grey.500" component="span">
                        {formatDialingCode(values.dialingCode || DIALING_CODES[0].value)}
                      </Box>
                    ),
                    endAdornment: (
                      <VerifyInfoButton
                        field="phone"
                        initialValue={initialValues.phone}
                        dialingCode={values.dialingCode}
                        phone={values.phone}
                        onConfirm={onAccountUpdate}
                      />
                    ),
                  }}
                />
              ) : (
                <VerifyInfoButton
                  field="phone"
                  initialValue={initialValues.phone}
                  dialingCode={values.dialingCode}
                  phone={values.phone}
                  onConfirm={onAccountUpdate}
                >
                  <TextField
                    className={classes.textField}
                    label={t('form.mobile_phone')}
                    id="phone-field"
                    name="phone"
                    fullWidth
                    value={encryptPhone(values.phone)}
                    placeholder={t('validation.please_enter', { field: '$t(form.mobile_phone)' })}
                    type="number"
                    error={isError('phone')}
                    // disabled: Firefox, and perhaps other browsers, disable DOM events on form fields that are disabled. Any event that starts at the disabled form field is completely canceled and does not propagate up the DOM tree.
                    readOnly
                    helperText={errorText('phone')}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      startAdornment: values.phone && (
                        <Box flexShrink={0} fontSize="body2.fontSize" color="grey.500" component="span">
                          {formatDialingCode(values.dialingCode || DIALING_CODES[0].value)}
                        </Box>
                      ),
                      endAdornment: <RightArrowIcon className={classes.arrowIcon} />,
                    }}
                  />
                </VerifyInfoButton>
              )}
              {initialValues.email ? (
                <TextField
                  className={classes.textField}
                  label={t('form.email')}
                  id="email-field"
                  name="email"
                  disabled
                  fullWidth
                  value={encryptEmail(values.email)}
                  placeholder={t('validation.please_enter', { field: '$t(form.email)' })}
                  error={isError('email')}
                  helperText={errorText('email')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    endAdornment: (
                      <VerifyInfoButton
                        field="email"
                        initialValue={initialValues.email}
                        email={values.email}
                        onConfirm={onAccountUpdate}
                      />
                    ),
                  }}
                />
              ) : (
                <VerifyInfoButton
                  field="email"
                  initialValue={initialValues.email}
                  email={values.email}
                  onConfirm={onAccountUpdate}
                >
                  <TextField
                    className={classes.textField}
                    label={t('form.email')}
                    id="email-field"
                    name="email"
                    disabled
                    fullWidth
                    value={encryptEmail(values.email)}
                    placeholder={t('validation.please_enter', { field: '$t(form.email)' })}
                    error={isError('email')}
                    helperText={errorText('email')}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      endAdornment: <RightArrowIcon className={classes.arrowIcon} />,
                    }}
                  />
                </VerifyInfoButton>
              )} */}
              <Box>
                {ACCOUNTS.map((item) => (
                  <Box key={item.key} mb={1}>
                    <Box my={1} display="flex" justifyContent="space-between" alignItems="center">
                      <Box className={classes.infoLabel} fontSize={isMobile ? 12 : 14}>
                        {item.key === 'phone' && <sup style={{ color: '#F2512D' }}>*</sup>}
                        {t(item.label)}
                        <Typography
                          variant="h4"
                          className={userInfo?.phone ? classes.infoText : classes.infoText}
                        >
                          {item.key === 'phone' && userInfo?.dialingCode ? `+${userInfo?.dialingCode} ` : ''}
                          <span style={{ color: userInfo && userInfo[item.key] ? '' : '#9CA3AF' }}>
                            {(userInfo && userInfo[item.key]) || t('profile.unset')}
                          </span>
                        </Typography>
                      </Box>
                      <Button
                        className={classes.editAccountButton}
                        variant="text"
                        disabled={userInfo && userInfo[item.key]}
                        onClick={toggleDrawer(item.key, true)}
                      >
                        {userInfo && userInfo[item.key] ? '' : t('profile.add')}
                      </Button>
                    </Box>
                    <Divider />
                    <EDrawer
                      onClose={toggleDrawer('phone', false)}
                      open={openDrawer.phone}
                      title={t('profile.edit') + t('profile.tel_number')}
                    >
                      <ChangePhoneForm
                        field="phone"
                        initialValue={initialValues.phone}
                        dialingCode={initialValues.dialingCode}
                        phone={userInfo?.phone}
                        onConfirm={toggleDrawer('phone', false)}
                      />
                    </EDrawer>
                    <EDrawer
                      onClose={toggleDrawer('email', false)}
                      open={openDrawer.email}
                      title={t('profile.edit') + t('form.address_email')}
                    >
                      <ChangePhoneForm
                        field="email"
                        initialValue={initialValues.email}
                        email={userInfo?.email}
                        onConfirm={toggleDrawer('email', false)}
                      />
                    </EDrawer>
                    {/* dialog for pc */}
                    <Dialog
                      maxWidth="xs"
                      open={openDialog}
                      fullWidth
                      onClose={() => {
                        setOpenDialog(false)
                      }}
                    >
                      <Box mb={2}>
                        <Box py={2} fontSize={20} fontWeight={600} textAlign="center">
                          {DIALING_Label[activeDialogKey]}
                        </Box>
                        <IconButton
                          className={classes.closeButton}
                          aria-label="close"
                          onClick={() => {
                            setOpenDialog(false)
                          }}
                          color="primary"
                        >
                          <CloseIcon />
                        </IconButton>
                        <Divider />
                      </Box>
                      <DialogContent className={classes.dialogContent}>
                        {activeDialogKey === 'phone' && (
                          <ChangePhoneForm
                            field="phone"
                            initialValue={initialValues.phone}
                            dialingCode={userInfo?.dialingCode}
                            phone={userInfo?.phone}
                            onConfirm={() => {
                              setOpenDialog(false)
                            }}
                          />
                        )}
                        {activeDialogKey === 'email' && (
                          <ChangePhoneForm
                            field="email"
                            initialValue={initialValues.email}
                            email={userInfo?.email}
                            onConfirm={() => {
                              setOpenDialog(false)
                            }}
                          />
                        )}
                      </DialogContent>
                    </Dialog>
                  </Box>
                ))}
              </Box>
              <Box display="none">
                <FixedButtonWrapper>
                  <Button
                    fullWidth
                    // type=“submit”
                    onClick={() => {
                      setFieldValue('phone', userInfo?.phone)
                      handleSubmit()
                    }}
                    id="submit"
                    variant="contained"
                    // disabled={!dirty || isSubmitting}
                    color="primary"
                    className={classes.submitButton}
                  >
                    {updateUserInfoStatus === 'pending' ? (
                      <CircularProgress color="inherit" size={24} />
                    ) : (
                      t('common.confirm')
                    )}
                  </Button>
                </FixedButtonWrapper>
              </Box>

              <FocusError />
            </form>
            <Dialog fullWidth maxWidth="sm" open={open} onClose={handleCancelSubmit}>
              <DialogTitle
                id="cancel-order-dialog"
                style={{
                  textAlign: 'center',
                  color: '#111',
                  background: '#fff',
                  borderBottom: '1px solid #E5E7EB',
                }}
              >
                {t('tips.main_tips')}
              </DialogTitle>
              <DialogContent>
                <Typography component="div" width="100%">
                  <Box fontSize="body1.fontSize" color="secondary.main" my={1}>
                    {t('tips.check_account_info')}
                  </Box>
                  <Box fontSize="body2.fontSize" fontWeight="fontWeightBold">
                    <Box>
                      {t('form.name')}: {values.nameEn || values.nameCn}
                    </Box>
                    <Box>
                      {t('form.id_number')}: {values.identifyId}
                    </Box>
                    <Box>
                      {t('form.birthday')}:{' '}
                      {isValid(values.birthday) && format(values.birthday, BIRTHDAY_FORMAT)}
                    </Box>
                    <Box>
                      {t('form.phone')}:{' '}
                      {userInfo?.phone ? formatDialingCode(values.dialingCode) + userInfo.phone : ''}
                    </Box>
                    <Box className={classes.emailBox} wordBreak="break-all">
                      {t('form.address_email')}:{userInfo?.email || values?.email}
                    </Box>
                  </Box>
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button variant="text" color="default" onClick={handleCancelSubmit}>
                  {t('common.back')}
                </Button>

                <Button type="submit" variant="contained" color="secondary" onClick={handleResolveSubmit}>
                  {t('common.confirm')}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )
      }}
    </Formik>
  )
}

export default UserInfoForm
